import type { FC } from "react"
import { useMemo } from "react"
import { alpha, Box, Button, Stack, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import type { InputRules } from "types/common.types"
import type { AlertSettingParam, ThresholdsField } from "types/alerts.types"
import type { TelemetryOptionType } from "types/telemetries.types"

import { getAlertTypeFields } from "helpers/utils/alerts"
import { getThresholdsUnit } from "widgets/alerts/AlertConfiguration/helpers"
import ThresholdInput from "./ThresholdInput"
import { getEmptyThreshold, getThresholdStatusOptions } from "./thresholds"

interface ThresholdFormProps {
  param: AlertSettingParam
  rules?: InputRules
}

const ThresholdForm: FC<ThresholdFormProps> = ({ param }) => {
  const { control } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    control,
    name: "thresholds",
  })
  const { t } = useTranslation()
  const [type, selectedFields] = useWatch({ control, name: ["type", "fields"] })
  const unit = useMemo(() => {
    const fieldNames = Array.isArray(selectedFields)
      ? (selectedFields as TelemetryOptionType[]).map(({ value }) => value)
      : undefined
    return getThresholdsUnit(getAlertTypeFields(type, fieldNames))
  }, [type, selectedFields])

  const statusDropdownOptions = useMemo(() => getThresholdStatusOptions(param), [param])
  const emptyThreshold = useMemo(() => getEmptyThreshold(param), [param])

  return (
    <>
      <Stack sx={{ width: "100%", mb: "1rem", gap: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {param.name}
        </Typography>
        {fields?.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              position: "relative",
              p: 1.5,
              pr: 5.5,
              border: 1,
              borderColor: "neutral.300",
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
            }}
          >
            <ThresholdInput
              key={field.id}
              remove={remove}
              index={index}
              value={field as ThresholdsField}
              control={control}
              statusDropdownOptions={statusDropdownOptions}
              unit={unit}
            />
          </Box>
        ))}
        <Box>
          <Button
            variant="text"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => append(emptyThreshold)}
          >
            {t("alert_settings.ADD_THRESHOLD")}
          </Button>
        </Box>
      </Stack>
    </>
  )
}

export default ThresholdForm
